import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { euro } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SubCardHint } from "@/components/subscription/SubPromo";
import { useLang, locProduct, productPath } from "@/lib/i18n";

export function ProductCard({ p: raw, index = 0, details = false }) {
  const { add } = useCart();
  const { lang, t } = useLang();
  const p = locProduct(raw, lang);
  const href = productPath(p.slug, lang);
  const out = details && p.stock === 0;
  return (
    <motion.article
      data-testid={`product-card-${p.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col"
    >
      <Link to={href} data-testid={`product-link-${p.slug}`} className="relative block aspect-[4/5] overflow-hidden rounded-sm bg-[var(--ink-2)]">
        <img src={p.image || undefined} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-50 blur-2xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(217,180,106,0.18),rgba(11,8,9,0.55)_70%)]" />
        <img src={p.image || undefined} alt={p.name} loading="lazy" className="relative h-full w-full object-contain p-4 drop-shadow-[0_20px_40px_rgba(0,0,0,0.55)] transition-transform duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.05]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[rgba(11,8,9,0.6)] via-transparent to-transparent" />
        {p.badge && <span className="absolute left-4 top-4 rounded-full border border-[var(--gold)]/50 bg-[rgba(11,8,9,0.6)] px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[var(--gold-soft)] backdrop-blur-md">{p.badge}</span>}
        <span className="absolute bottom-4 left-4 text-[11px] uppercase tracking-[0.25em] text-[var(--parchment)]/80">{t(p.category)}</span>
      </Link>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl leading-tight">{p.name}</h3>
          <p className="mt-1 line-clamp-2 text-sm text-[var(--muted)]">{p.tagline}</p>
          {lang === "en" ? null : <SubCardHint p={p} />}
          {!details && p.preorder ? <p data-testid={`product-preorder-${p.slug}`} className="mt-2 text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">{lang === "en" ? "Pre-order" : "Précommande"}</p> : null}
          {details ? (
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--parchment)]/80">
              {p.volume ? <span data-testid={`product-volume-${p.slug}`}>{p.volume}</span> : null}
              <span data-testid={`product-stock-${p.slug}`} className={`flex items-center gap-1.5 ${out ? "text-red-300" : p.preorder ? "text-[var(--gold-soft)]" : "text-emerald-300"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${out ? "bg-red-300" : "bg-emerald-300"}`} aria-hidden="true" />{p.preorder ? (lang === "en" ? "Pre-order" : "Précommande") : out ? (lang === "en" ? "Out of stock" : "Rupture de stock") : lang === "en" ? "In stock" : "En stock"}
              </span>
            </p>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <p data-testid={`product-price-${p.slug}`} className="font-display text-2xl text-[var(--gold-soft)]">{euro(p.price)}</p>
          {p.compare_at ? <p className="text-xs text-[var(--muted)] line-through">{euro(p.compare_at)}</p> : null}
        </div>
      </div>
      {details ? (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={href} data-testid={`discover-${p.slug}`} aria-label={p.name}
            className="flex h-11 items-center justify-center rounded-full border border-[var(--line)] text-xs uppercase tracking-[0.22em] transition-[border-color,color] duration-300 hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">
            {lang === "en" ? "View" : "Découvrir"}
          </Link>
          <button data-testid={`add-to-cart-${p.slug}`} disabled={out} onClick={() => add(p)} aria-label={`Ajouter ${p.name} au panier`}
            className="flex h-11 items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ink)] transition-[background-color] duration-300 hover:bg-[var(--gold-soft)] disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> {t("Ajouter")}
          </button>
        </div>
      ) : (
      <button
        data-testid={`add-to-cart-${p.slug}`}
        onClick={() => add(p)}
        className="mt-4 flex h-11 items-center justify-center gap-2 rounded-full border border-[var(--line)] text-xs uppercase tracking-[0.22em] transition-[background-color,color,border-color] duration-300 hover:border-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ink)]"
      >
        <Plus className="h-3.5 w-3.5" /> {t("Ajouter")}
      </button>
      )}
    </motion.article>
  );
}
